
import { motion } from 'framer-motion';
import { Award, BarChart, BookOpen, Calendar, Clock, Star, TrendingUp, Trophy } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AreaChart, XAxis, YAxis, CartesianGrid, Tooltip, Area, ResponsiveContainer, BarChart as RBarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { PageLayout } from '@/components/layout/PageLayout';

const Progress = () => {
  // Sample data for charts
  const activityData = [
    { day: 'Mon', minutes: 15, topics: 2 },
    { day: 'Tue', minutes: 25, topics: 3 },
    { day: 'Wed', minutes: 20, topics: 2 },
    { day: 'Thu', minutes: 30, topics: 4 },
    { day: 'Fri', minutes: 15, topics: 1 },
    { day: 'Sat', minutes: 5, topics: 1 },
    { day: 'Sun', minutes: 10, topics: 1 },
  ];
  
  const categoryData = [
    { name: 'Fundamentals', value: 35 },
    { name: 'Strategy', value: 25 },
    { name: 'Tools', value: 20 },
    { name: 'Ethics', value: 10 },
    { name: 'Implementation', value: 10 },
  ];
  
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];
  
  const achievements = [
    { id: 1, title: 'Fast Learner', description: 'Completed 3 modules in a week', icon: <BookOpen className="h-10 w-10" />, date: '2 days ago' },
    { id: 2, title: 'AI Explorer', description: 'Tried 5 different AI tools', icon: <Star className="h-10 w-10" />, date: '1 week ago' },
    { id: 3, title: 'Consistent', description: '5-day learning streak', icon: <TrendingUp className="h-10 w-10" />, date: '1 week ago' },
  ];

  return (
    <PageLayout>
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold sf-pro">Your Progress</h1>
          <p className="text-muted-foreground">Track your AI learning journey</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard 
            icon={<Calendar className="h-5 w-5" />}
            title="Learning Streak"
            value="5 days"
            description="Keep going!"
            color="blue"
          />
          
          <StatCard 
            icon={<Clock className="h-5 w-5" />}
            title="This Week"
            value="120 minutes"
            description="15% more than last week"
            color="green"
            trend="up"
          />
          
          <StatCard 
            icon={<BookOpen className="h-5 w-5" />}
            title="Modules Completed"
            value="4"
            description="Out of 12 total modules"
            color="purple"
          />
          
          <StatCard 
            icon={<Trophy className="h-5 w-5" />}
            title="Achievements"
            value="3"
            description="Earned so far"
            color="amber"
          />
        </div>

        <Tabs defaultValue="activity" className="w-full mb-8">
          <TabsList className="grid w-full max-w-md grid-cols-3 mb-8">
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="focus">Focus Areas</TabsTrigger>
            <TabsTrigger value="achievements">Achievements</TabsTrigger>
          </TabsList>
          
          {/* Activity Tab */}
          <TabsContent value="activity" className="space-y-8">
            <Card className="glass-card">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-xl sf-pro">Weekly Activity</CardTitle>
                    <CardDescription>Time spent on AI training (minutes)</CardDescription>
                  </div>
                  <div className="flex items-center text-sm font-medium bg-primary/10 text-primary px-3 py-1 rounded-full">
                    <BarChart className="h-4 w-4 mr-1" />
                    Last 7 days
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={activityData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorMinutes" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0070F3" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#0070F3" stopOpacity={0.1} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="day" />
                      <YAxis />
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="minutes"
                        stroke="#0070F3"
                        fillOpacity={1}
                        fill="url(#colorMinutes)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Focus Areas Tab */}
          <TabsContent value="focus" className="space-y-8">
            <Card className="glass-card">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-xl sf-pro">Focus Areas</CardTitle>
                    <CardDescription>Distribution of your learning topics</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="h-[300px] flex items-center justify-center">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={categoryData}
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={100}
                          fill="#8884d8"
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {categoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-col justify-center">
                    <h3 className="text-lg font-semibold mb-4 sf-pro">Your Focus Distribution</h3>
                    <div className="space-y-4">
                      {categoryData.map((category, index) => (
                        <div key={category.name} className="space-y-1">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center">
                              <div 
                                className="w-3 h-3 rounded-full mr-2" 
                                style={{ backgroundColor: COLORS[index % COLORS.length] }}
                              ></div>
                              <span className="text-sm">{category.name}</span>
                            </div>
                            <span className="text-sm font-medium">{category.value}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 text-sm text-muted-foreground">
                      <p>Your learning is focused on core AI fundamentals and strategic implementation, which is ideal for a C-level executive.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Achievements Tab */}
          <TabsContent value="achievements" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {achievements.map((achievement) => (
                <AchievementCard key={achievement.id} achievement={achievement} />
              ))}
              <Card className="glass-card h-full flex flex-col items-center justify-center text-center p-8 border-dashed border-2">
                <Award className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium sf-pro mb-2">More to Unlock</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Continue your AI training to unlock more achievements
                </p>
                <p className="text-xs text-muted-foreground">
                  Complete daily challenges to progress faster
                </p>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
};

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: string;
  description: React.ReactNode;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
}

const StatCard = ({ icon, title, value, description, color, trend }: StatCardProps) => {
  const getColorClass = () => {
    switch (color) {
      case 'blue': return 'bg-blue-50 text-blue-600';
      case 'green': return 'bg-green-50 text-green-600';
      case 'purple': return 'bg-purple-50 text-purple-600';
      case 'amber': return 'bg-amber-50 text-amber-600';
      default: return 'bg-primary/10 text-primary';
    }
  };

  const getTrendIcon = () => {
    if (!trend) return null;
    
    if (trend === 'up') {
      return <TrendingUp className="h-4 w-4 text-green-500" />;
    } else if (trend === 'down') {
      return <TrendingUp className="h-4 w-4 text-red-500 transform rotate-180" />;
    }
    return null;
  };

  return (
    <motion.div
      whileHover={{ y: -5 }}
      transition={{ type: "spring", stiffness: 300 }}
      className="glass-card rounded-xl p-5"
    >
      <div className="flex justify-between items-start mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${getColorClass()}`}>
          {icon}
        </div>
        {getTrendIcon()}
      </div>
      <h3 className="text-sm font-medium text-muted-foreground mb-1">{title}</h3>
      <p className="text-2xl font-bold sf-pro">{value}</p>
      <div className="mt-1 text-sm text-muted-foreground">{description}</div>
    </motion.div>
  );
};

interface Achievement {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  date: string;
}

const AchievementCard = ({ achievement }: { achievement: Achievement }) => {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      transition={{ type: "spring", stiffness: 300 }}
      className="glass-card rounded-xl p-6 flex flex-col items-center text-center"
    >
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
        {achievement.icon}
      </div>
      <h3 className="text-lg font-semibold mb-1 sf-pro">{achievement.title}</h3>
      <p className="text-sm text-muted-foreground mb-2">{achievement.description}</p>
      <div className="mt-auto pt-4">
        <p className="text-xs text-muted-foreground">Earned {achievement.date}</p>
      </div>
    </motion.div>
  );
};

export default Progress;
